import { composeWithDevTools } from '@redux-devtools/extension';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import { thunk } from 'redux-thunk';
import type { PractitionerRole } from '@common/enums/practitionerRole';
import type { ServicePermissions } from '@common/interfaces';
import type { IManagerRole } from '@common/interfaces/managerRole';
import type { Profession } from '../common/enums/profession';
import type { IAuthenticationState } from './store/authentication/reducers';
import type { ILanguageState } from './store/language/reducers';
import type { IServiceConsumerPayload } from './store/serviceConsumers/serviceConsumerActions';
import type { IServiceProviderPayload } from './store/serviceProviders/serviceProviderActions';
import type { ThunkState } from './store/utils/thunkState';
import { rootReducer as authenticationRootReducer } from './store/authentication/reducers';
import { rootReducer as languageRootReducer } from './store/language/reducers';
import { serviceConsumerReducer } from './store/serviceConsumers/serviceConsumerReducers';
import { serviceProviderReducer } from './store/serviceProviders/serviceProviderReducers';

function logger() {
    return (next: any) => (action: any) => {
        const returnValue = next(action);

        return returnValue;
    };
}

export function configureStore(initialState: any) {
    return composeWithDevTools(applyMiddleware(thunk, logger))(createStore as any)(
        combineReducers({
            authentication: authenticationRootReducer,
            language: languageRootReducer,
            serviceConsumers: serviceConsumerReducer,
            serviceProviders: serviceProviderReducer,
        }),
        initialState
    );
}

export const store = configureStore({});

export interface IReduxState {
    authentication: ISessionUser;
    language: ILanguageState;
    routing: any;
    serviceConsumers: ThunkState<IServiceConsumerPayload>;
    serviceProviders: ThunkState<IServiceProviderPayload>;
}

export interface IMaybeLoggedInReduxState {
    authentication: IAuthenticationState;
    language: ILanguageState;
    routing: any;
    serviceConsumers: ThunkState<IServiceConsumerPayload>;
    serviceProviders: ThunkState<IServiceProviderPayload>;
}

export interface ISessionUser {
    areContactsAndTasksEnabled: boolean;
    areEmploymentTaxesDetailsRequired: boolean;
    emailAddress: string;
    employmentPractitionerRoles?: PractitionerRole[];
    groupManagerId: string | null;
    hasEmploymentRights: boolean;
    hasKsyosEmployment: boolean;
    id: string;
    isChecking: boolean;
    isGeneralPractitioner: boolean;
    isGroupManager: boolean;
    isKsyos: boolean;
    isManager: boolean;
    isSupervisor: boolean;
    // @TODO CS-19380 remove isSystemManager when the test makeUploadJsonTest.ts is rewritten
    isSystemManager: boolean;
    language: string;
    loggedIn: boolean;
    managerRole: IManagerRole | null;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
    needsSupervisor: boolean;
    organizationHasGeneralPractitionerOrSupervisor: boolean;
    organizationId: string;
    organizationType: string;
    profession: Profession;
    profilePictureUrl?: string;
    selectedEmploymentId: string;
    servicePermissions: ServicePermissions;
    token: string;
    userName: string;
}
